import { useEffect, useRef, useState } from "react";
import { Link, useLocation } from "react-router";
import type { MessageKey } from "@wato/i18n";
import { useI18n } from "@wato/i18n/react";
import { Alert, Stack } from "@wato/ui";
import { ApiError } from "../../api/client";
import { useApi } from "../../api/context";
import { AuthLayout } from "../../components/common";

/** The confirmation link carries the token in the URL fragment (`#token=…`), like password reset. */
function tokenFromHash(hash: string): string | null {
  const token = new URLSearchParams(hash.replace(/^#/, "")).get("token");
  return token && token.length > 0 ? token : null;
}

type Outcome =
  | { readonly kind: "working" }
  | { readonly kind: "done"; readonly email: string }
  | {
      readonly kind: "error";
      readonly state: "expired" | "used" | "invalid" | "inUse" | "failed";
    };

function errorState(error: unknown): "expired" | "used" | "invalid" | "inUse" | "failed" {
  if (!(error instanceof ApiError)) return "failed";
  switch (error.code) {
    case "AUTH_EMAIL_CHANGE_EXPIRED":
      return "expired";
    case "AUTH_EMAIL_CHANGE_USED":
      return "used";
    case "AUTH_EMAIL_CHANGE_INVALID":
    case "AUTH_VALIDATION_FAILED":
      return "invalid";
    case "AUTH_EMAIL_IN_USE":
      return "inUse";
    default:
      return "failed";
  }
}

/**
 * Q4-ONB-09: landing page of the email-change confirmation link (`/confirm-email#token=…`).
 * Public and outside the workspace shell: the link itself proves control of the new address.
 */
export function ConfirmEmailPage() {
  const { t } = useI18n();
  const api = useApi();
  const location = useLocation();
  // The router's location can lag a fragment-only navigation (we strip the token with
  // history.replaceState), so also follow `hashchange` (Q4V4-03).
  const [liveHash, setLiveHash] = useState<string | null>(null);
  useEffect(() => {
    const onHash = () => setLiveHash(window.location.hash);
    window.addEventListener("hashchange", onHash);
    return () => window.removeEventListener("hashchange", onHash);
  }, []);
  const token = tokenFromHash(liveHash ?? location.hash);
  const [outcome, setOutcome] = useState<Outcome>(
    token ? { kind: "working" } : { kind: "error", state: "invalid" },
  );
  // The token is single-use: never send the same one twice (StrictMode re-runs effects in
  // development), but a different token in a later URL is processed.
  const sent = useRef<string | null>(null);

  useEffect(() => {
    if (!token) {
      if (liveHash !== null) setOutcome({ kind: "error", state: "invalid" });
      return;
    }
    if (sent.current === token) return;
    sent.current = token;
    setOutcome({ kind: "working" });
    // Drop the token from the address bar/history as soon as it is read.
    window.history.replaceState(null, "", location.pathname);
    api.confirmEmailChange(token).then(
      (result) => setOutcome({ kind: "done", email: result.email }),
      (error: unknown) => setOutcome({ kind: "error", state: errorState(error) }),
    );
  }, [api, token, liveHash, location.pathname]);

  return (
    <AuthLayout title={t("dashboard.confirmEmail.title")}>
      <div data-testid="confirm-email" aria-live="polite">
        {outcome.kind === "working" && (
          <p className="text-sm text-fg-muted">{t("dashboard.confirmEmail.working")}</p>
        )}
        {outcome.kind === "done" && (
          <Stack gap={3}>
            <Alert tone="success" title={t("dashboard.confirmEmail.doneTitle")}>
              {t("dashboard.confirmEmail.doneBody", { email: outcome.email })}
            </Alert>
            <Link className="font-semibold text-on-action-ghost underline" to="/login">
              {t("dashboard.confirmEmail.signIn")}
            </Link>
          </Stack>
        )}
        {outcome.kind === "error" && (
          <Stack gap={3}>
            <Alert
              tone="error"
              title={t(`dashboard.confirmEmail.${outcome.state}Title` as MessageKey)}
            >
              {t(`dashboard.confirmEmail.${outcome.state}Body` as MessageKey)}
            </Alert>
            <Link className="font-semibold text-on-action-ghost underline" to="/account">
              {t("dashboard.confirmEmail.toProfile")}
            </Link>
          </Stack>
        )}
      </div>
    </AuthLayout>
  );
}
